import React, {
    useEffect,
    useState,
    useCallback,
    useMemo,
    useImperativeHandle,
    forwardRef
} from 'react';
import config from '../config/config';
import {
    Grid,
    GridColumn,
    GridToolbar,
    GridDetailRowProps,
    GridExpandChangeEvent
} from '@progress/kendo-react-grid';
import {
    Card,
    CardHeader,
    CardTitle,
    CardSubtitle,
    CardBody
} from '@progress/kendo-react-layout';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import styled from 'styled-components';
import { createGlobalStyle } from 'styled-components';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { SvgIcon } from '@progress/kendo-react-common';
import { trashIcon, saveIcon } from '@progress/kendo-svg-icons';
import axios from 'axios';
import { useForm } from '../contexts/FormContext';
import {
    Notification,
    NotificationGroup
} from '@progress/kendo-react-notification';
import GetUserAvatar from '../components/GetUserAvatar';
import MobileDetailRow, {
    GridRow as DetailGridRow
} from '../components/MobileDetailRow';

/* ------------------------------------------------------------------ */
/* Typen & Konstanten                                                 */
/* ------------------------------------------------------------------ */
interface TaskDef {
    Position: number;
    Beschreibung: string;
    Einheit: string;
    DetailText?: string; 
}

interface TaskMeta { Einheit: string; DetailText: string }
interface GridRow extends DetailGridRow {
    DetailText?: string;
}

export interface ModuleStep2Ref {
    saveAll: () => Promise<void>;
}

const FORM_DATA_INDEX = 'formDataIndex';
const DATA_ITEM_KEY = 'Position';
const initialSort: SortDescriptor[] = [{ field: 'Position', dir: 'asc' }];

const StyledButton = styled(Button)`
    display: block;
    margin-left: auto;
    font-weight: bold;
    font-size: 20px;
`;

const BigExpandIconStyle = createGlobalStyle`
  @media (max-width: 679px) {
    .k-hierarchy-cell .k-svg-icon { width: 24px; height: 24px; }
    .k-hierarchy-cell .k-icon     { font-size: 24px; }
  }
`;

const BigDropdownFont = createGlobalStyle`
  .big-dropdown .k-list-item,
  .big-dropdown .k-list-item .k-link { font-size: 16px; }
  .k-dropdownlist .k-input-inner     { font-size: 16px; }
`;

/* ------------------------------------------------------------------ */
/* Hilfs-Hook: Mobile-Breakpoint                                      */
/* ------------------------------------------------------------------ */
const useIsMobile = (breakpoint = 680) => {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    );
    useEffect(() => {
        const cb = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', cb);
        return () => window.removeEventListener('resize', cb);
    }, [breakpoint]);
    return isMobile;
};

/* ------------------------------------------------------------------ */
/* Aufwand-Cell (Desktop)                                             */
/* ------------------------------------------------------------------ */
const AufwandCell = React.memo(
    ({
        dataItem,
        field,
        dataIndex,
        onChange
    }: {
        dataItem: any;
        field: string;
        dataIndex: number;
        onChange: (
            e: InputChangeEvent,
            dataIndex: number,
            field: string
        ) => void;
    }) => (
        <Input
            value={dataItem[field] || ''}
            onChange={(e: InputChangeEvent) => onChange(e, dataIndex, field)}
            style={{ width: '100%' }}
        />
    )
);

/* ------------------------------------------------------------------ */
/* Hauptkomponente                                                    */
/* ------------------------------------------------------------------ */
const ModuleStep2 = forwardRef<ModuleStep2Ref>((_, ref) => {
    const isMobile = useIsMobile();
    const isiOS =
        typeof navigator !== 'undefined' &&
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !('MSStream' in window);

    const { formData } = useForm();

    const [taskMap, setTaskMap] = useState<Record<string, TaskMeta>>({});
    const [descriptionOptions, setDescriptionOptions] = useState<string[]>([]);
    const [dataState, setDataState] = useState<GridRow[]>([]);
    const [sort, setSort] = useState<SortDescriptor[]>(initialSort);
    const [showNotification, setShowNotification] = useState(false);

    /* -------------------------------------------------------------- */
    /* 1) Tasks laden                                                 */
    /* -------------------------------------------------------------- */
    useEffect(() => {
        axios
            .get(`${config.host}/tasks`)
            .then(res => {
                const raw = res.data;
                const arr: TaskDef[] = Array.isArray(raw)
                    ? raw
                    : Object.values(raw || {}).map(
                          (v: any, idx: number): TaskDef => ({
                              Position: idx + 1,
                              Beschreibung:
                                  typeof v === 'string'
                                      ? v
                                      : v?.Beschreibung ?? '',
                              Einheit:
                                  typeof v === 'string' ? '' : v?.Einheit ?? '',
                              DetailText:                           // ← NEU
                                  typeof v === 'string'
                                      ? ''
                                      : v?.DetailText ?? ''          // ← NEU
                          })
                      );

                setDescriptionOptions(arr.map(t => t.Beschreibung));
                setTaskMap(
                    arr.reduce<Record<string, TaskMeta>>((acc, t) => {
                        acc[t.Beschreibung] = {                  // ← Ein Objekt statt String
                            Einheit:     t.Einheit,
                            DetailText:  t.DetailText ?? ''      // ← NEU
                        };
                        return acc;
                    }, {})
                );
            })
            .catch(err =>
                console.error('❌ Fehler beim Laden der Tasks:', err)
            );
    }, []);

    /* -------------------------------------------------------------- */
    /* 2) Job-Tätigkeiten laden                                       */
    /* -------------------------------------------------------------- */
    useEffect(() => {
        if (!formData.jobID) return;
        axios
            .get(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`)
            .then(res => {
                const job = res.data;
                const rows: GridRow[] = Object.entries(job)
                    .filter(([k]) => k.startsWith('TODO'))
                    .map(([k, v]: [string, any]) => {
                        const index = parseInt(k.replace('TODO', ''), 10) - 1;
                        return {
                            Position: (index + 1).toString().padStart(3, '0'),
                            Aufwand: String(v.Aufwand ?? ''),
                            Einheit: v.Einheit ?? '',
                            Beschreibung: v.Beschreibung ?? '',
                            DetailText: v.DetailText ?? '',
                            expanded: false,
                            [FORM_DATA_INDEX]: index
                        };
                    });
                setDataState(rows);
            })
            .catch(err =>
                console.error('❌ Fehler beim Laden der Job-Tätigkeiten:', err)
            );
    }, [formData.jobID]);

    /* -------------------------------------------------------------- */
    /* 3) Handlers                                                    */
    /* -------------------------------------------------------------- */
    const handleDescriptionChange = useCallback(
        (event: any, dataIndex: number) => {
            const desc = event.value;
            const { Einheit: unit = '', DetailText: detail = '' } = taskMap[desc] ?? {};
            setDataState(prev => {
                const copy = [...prev];
                copy[dataIndex] = {
                    ...copy[dataIndex],
                    Beschreibung: desc,
                    Einheit:      unit,
                    DetailText:   detail            // ← NEU
                };
                return copy;
            });
        },
        [taskMap]
    );

    const handleAufwandChange = useCallback(
        (
            e: InputChangeEvent,
            idx: number,
            field: string = 'Aufwand'
        ) => {
            setDataState(prev => {
                const copy = [...prev];
                copy[idx] = {
                    ...copy[idx],
                    [field]: String(e.target.value ?? '')
                };
                return copy;
            });
        },
        []
    );

    /* --- für Desktop-Grid in <td>-Zelle --- */
    const memoizedAufwandCell = useMemo(
        () => (cellProps: any) => (
            <AufwandCell
                {...cellProps}
                dataIndex={cellProps.dataItem[FORM_DATA_INDEX]}
                onChange={handleAufwandChange}
            />
        ),
        [handleAufwandChange]
    );

    const handleExpandChange = useCallback(
        (e: GridExpandChangeEvent) => {
            const row = e.dataItem as GridRow;
            row.expanded = !row.expanded;
            setDataState([...dataState]);
        },
        [dataState]
    );

    /* -------------------------------------------------------------- */
    /* 4) Zeilen-Operationen                                          */
    /* -------------------------------------------------------------- */
    const onRowAction = useCallback(
        async ({
            rowIndex,
            operation,
            dataItem
        }: {
            rowIndex: number;
            operation: 'save' | 'remove' | 'add';
            dataItem?: GridRow;
        }) => {
            let next = [...dataState];

            if (operation === 'remove') {
                const deleteKey = `TODO${(rowIndex + 1)
                    .toString()
                    .padStart(2, '0')}`;
                await axios.delete(
                    `${config.host}/jobs/${encodeURIComponent(
                        formData.jobID
                    )}/${deleteKey}`
                );
                next = next
                    .filter((_, i) => i !== rowIndex)
                    .map((r, i) => ({
                        ...r,
                        Position: (i + 1).toString().padStart(3, '0'),
                        [FORM_DATA_INDEX]: i
                    }));
            }

            if (operation === 'save' && dataItem) {
            const key = `TODO${(rowIndex + 1).toString().padStart(2, '0')}`;
            next[rowIndex] = dataItem;

            // Teil-Update per Feldpfade, damit DetailText & Co. nicht gelöscht werden
            const partial: Record<string, any> = {};
            partial[`${key}.POS`]          = dataItem.Position;
            partial[`${key}.Aufwand`]      = dataItem.Aufwand;
            partial[`${key}.Einheit`]      = dataItem.Einheit;
            partial[`${key}.Beschreibung`] = (dataItem.Beschreibung || '').trim();
            // KEIN DetailText senden → bleibt bestehen / wird serverseitig befüllt

            await axios.put(
                `${config.host}/jobs/${encodeURIComponent(formData.jobID)}`,
                partial
            );

            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 3000);
            }


            if (operation === 'add') {
                const newIdx = next.length;
                next.push({
                    Position: (newIdx + 1).toString().padStart(3, '0'),
                    Aufwand: '',
                    Einheit: '',
                    Beschreibung: '',
                    expanded: false,
                    [FORM_DATA_INDEX]: newIdx
                });
            }

            setDataState(next);
        },
        [dataState, formData.jobID]
    );

    /* -------------------------------------------------------------- */
    /* 5) saveAll (via ref)                                           */
    /* -------------------------------------------------------------- */
    const saveAll = useCallback(async () => {
    if (!formData.jobID || !dataState.length) return;

    // Teil-Updates: pro Zeile Feldpfade setzen, keine vollständigen Maps
    const payload: Record<string, any> = {};
    dataState.forEach((row, idx) => {
        const key = `TODO${(idx + 1).toString().padStart(2, '0')}`;
        payload[`${key}.POS`]          = row.Position;
        payload[`${key}.Aufwand`]      = row.Aufwand;
        payload[`${key}.Einheit`]      = row.Einheit;
        payload[`${key}.Beschreibung`] = (row.Beschreibung || '').trim();
        // KEIN DetailText hier → bleibt bestehen / wird serverseitig ergänzt
    });

    await axios.put(
        `${config.host}/jobs/${encodeURIComponent(formData.jobID)}`,
        payload
    );
    }, [dataState, formData.jobID]);


    useImperativeHandle(ref, () => ({ saveAll }));

    /* -------------------------------------------------------------- */
    /* 6) Detail-Komponente Wrapper                                   */
    /* -------------------------------------------------------------- */
    const commitAufwand = useCallback(
        (value: string, index: number) => {
            handleAufwandChange(
                { target: { value } } as unknown as InputChangeEvent,
                index,
                'Aufwand'
            );
        },
        [handleAufwandChange]
    );

    /* stabile Detail-Komponente ------------------------- */
    const MobileDetailComponent = useCallback(
        (props: GridDetailRowProps) => {
            const row = props.dataItem as GridRow;
            return (
                <MobileDetailRow
                    dataItem={row}
                    idx={row[FORM_DATA_INDEX]}
                    commitAufwand={commitAufwand}
                />
            );
        },
        [commitAufwand]   // bleibt stabil
    );

    /* -------------------------------------------------------------- */
    /* 7) Grids                                                       */
    /* -------------------------------------------------------------- */
    const renderDesktopGrid = () => (
        <Grid
            data={orderBy(dataState, sort)}
            dataItemKey={DATA_ITEM_KEY}
            sortable
            sort={sort}
            onSortChange={e => setSort(e.sort)}
        >
            <GridToolbar>
                <StyledButton
                    themeColor="primary"
                    style={{ width: '100%', margin: '20px 0' }}
                    onClick={() =>
                        onRowAction({
                            rowIndex: dataState.length,
                            operation: 'add'
                        })
                    }
                >
                    Position hinzufügen
                </StyledButton>
            </GridToolbar>

            <GridColumn title="Position" field="Position" width="100px" />
            <GridColumn
                title="Aufwand"
                field="Aufwand"
                cell={memoizedAufwandCell}
                width="100px"
            />
            <GridColumn title="Einheit" field="Einheit" width="100px" />
            <GridColumn
                title="Beschreibung"
                field="Beschreibung"
                cell={props => {
                    const idx = props.dataItem[FORM_DATA_INDEX];
                    return (
                        <td>
                            <DropDownList
                                data={descriptionOptions}
                                value={props.dataItem.Beschreibung}
                                onChange={e =>
                                    handleDescriptionChange(e, idx)
                                }
                                style={{ width: '100%' }}
                            />
                        </td>
                    );
                }}
            />
            <GridColumn
                title="Befehl"
                cell={props => {
                    const idx = props.dataItem[FORM_DATA_INDEX];
                    return (
                        <td className="k-command-cell">
                            <Button
                                onClick={() =>
                                    onRowAction({
                                        rowIndex: idx,
                                        operation: 'save',
                                        dataItem: props.dataItem
                                    })
                                }
                            >
                                <SvgIcon icon={saveIcon} />
                            </Button>
                            <Button
                                themeColor="base"
                                onClick={() =>
                                    onRowAction({
                                        rowIndex: idx,
                                        operation: 'remove'
                                    })
                                }
                            >
                                <SvgIcon icon={trashIcon} />
                            </Button>
                        </td>
                    );
                }}
                width="110px"
            />
        </Grid>
    );

    const renderMobileGrid = () => (
        <Grid
            data={orderBy(dataState, sort)}
            dataItemKey={DATA_ITEM_KEY}
            detail={MobileDetailComponent}
            expandField="expanded"
            onExpandChange={handleExpandChange}
            style={{ minHeight: '400px' }}
        >
            <GridToolbar>
                <StyledButton
                    themeColor="primary"
                    style={{ width: '100%', margin: '20px 0' }}
                    onClick={() =>
                        onRowAction({
                            rowIndex: dataState.length,
                            operation: 'add'
                        })
                    }
                >
                    Position hinzufügen
                </StyledButton>
            </GridToolbar>

            <GridColumn
                title="Beschreibung"
                field="Beschreibung"
                width={240}
                cell={props => {
                    const idx = props.dataItem[FORM_DATA_INDEX];
                    return (
                        <td>
                            {isiOS ? (
                                <select
                                    value={props.dataItem.Beschreibung}
                                    onChange={e =>
                                        handleDescriptionChange(
                                            { value: e.target.value },
                                            idx
                                        )
                                    }
                                    style={{
                                        width: '100%',
                                        height: 32,
                                        fontSize: 16
                                    }}
                                >
                                    {descriptionOptions.map(opt => (
                                        <option key={opt} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <DropDownList
                                    data={descriptionOptions}
                                    value={props.dataItem.Beschreibung}
                                    onChange={e =>
                                        handleDescriptionChange(e, idx)
                                    }
                                    style={{ width: '100%' }}
                                    popupSettings={{ className: 'big-dropdown' }}
                                />
                            )}
                        </td>
                    );
                }}
            />

            <GridColumn
                title="Befehl"
                width="90px"
                cell={props => {
                    const idx = props.dataItem[FORM_DATA_INDEX];
                    return (
                        <td className="k-command-cell">
                            <Button
                                themeColor="base"
                                onClick={() =>
                                    onRowAction({
                                        rowIndex: idx,
                                        operation: 'remove'
                                    })
                                }
                            >
                                <SvgIcon icon={trashIcon} />
                            </Button>
                        </td>
                    );
                }}
            />
        </Grid>
    );

    /* -------------------------------------------------------------- */
    /* 8) Render-Wrapper                                              */
    /* -------------------------------------------------------------- */
    return (
        <div style={{ padding: 5 }}>
            <BigExpandIconStyle />
            <BigDropdownFont />
            {showNotification && (
                <NotificationGroup
                    style={{
                        position: 'fixed',
                        top: 30,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000
                    }}
                >
                    <Notification type={{ style: 'success', icon: true }}>
                        <span>Tätigkeit wurde erfolgreich gespeichert.</span>
                    </Notification>
                </NotificationGroup>
            )}

            <Card orientation="vertical">
                <CardHeader className="k-hbox custom-card-header">
                    <GetUserAvatar />
                    <div>
                        <CardTitle>Step 2: Tätigkeiten</CardTitle>
                        <CardSubtitle>
                            Bitte trage deine Tätigkeiten hier ein
                        </CardSubtitle>
                    </div>
                </CardHeader>
                <CardBody>
                    {isMobile ? renderMobileGrid() : renderDesktopGrid()}
                </CardBody>
            </Card>
        </div>
    );
});

export default ModuleStep2;
